import { useTranslate } from "helpers";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ContactEmailCardProps, ContactEmailRef } from "./types";
import { NoResults } from "ui/components/NoResults";
import ContactEmailSearchResults from "./components/ContactEmailSearchResults/ContactEmailSearchResults";
import ContactEmailEditCard from "../ContactEmailEditCard/ContactEmailEditCard";
import { deleteContactEmailAPI, getContactEmailsAPI } from "services/api/contacts/api";
import { ContactEmailRow } from "services/api/contacts/types";
import { Dialog } from "ui/components/Dialog";


const ContactEmailCard = forwardRef<ContactEmailRef, ContactEmailCardProps>((props, ref) => {
  const { contactDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactEmailRow>
  >(initialDataWithPagination);

  const [emailId, setEmailId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
  }, [contactDetail.id]);

  const fetchSearchResult = async (pagination: Pagination, contactId: string) => {
    setIsLoading(true);
    try {
      const results = await getContactEmailsAPI(pagination, contactId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    if (id) {
      setEmailId(id);
      setShowAddNewModal(true);
    }
  }, []);

  const openAddNew = () => {
    setEmailId("");
    setShowAddNewModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
    setShowAddNewModal(false)
  }, [contactDetail.id, searchResults.pagination]);

  const handleEmailClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, emailAddress: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `mailto:${emailAddress}`;
  }

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setEmailId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!emailId) return;

    try {
      if (contactDetail.id) {
        const deleted = await deleteContactEmailAPI(
          t,
          contactDetail.id,
          emailId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, contactDetail.id);
        }
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setEmailId("");
    } catch (err) {
      console.error("Greška prilikom brisanja email-a:", err);
    }
  }, [emailId, t, contactDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setEmailId("");
    setOpenDialog(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!contactDetail.id) return <></>;

  return (
    <>

        <div className="mx-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.contact.email.search.noresults")} />
          ) : (
            <ContactEmailSearchResults
              searchResults={searchResults}
              selectedRows={[]}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onDeleteClick={handleDeleteClick}
              onEmailClick={handleEmailClick}
            />
          )}
        </div>

      <ContactEmailEditCard
        emailId={emailId}
        contactId={contactDetail.id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.email.dialog.delete.title"
        )}
        message={t(
          "admin.contact.email.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.email.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.email.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(ContactEmailCard);

